<template>
    <div>
        <div class="lg:grid lg:grid-cols-12 lg:gap-x-5">
            <ControlsMenuComponent class="sm:mb-6" />

            <aside class="px-0 py-3 sm:px-6 lg:col-span-3 lg:px-0 lg:py-0">
                <nav class="space-y-1 sticky top-20">
                    <a v-for="item in subNavigation" :key="item.name" :href="`#${item.id}`" @click.prevent="currentSection = item.id" :class="[currentSection == item.id ? 'bg-white text-blue-600 hover:bg-white' : 'text-gray-900 hover:bg-gray-50 hover:text-gray-900', 'group flex items-center rounded-md px-3 py-2 text-sm font-medium']" :aria-current="currentSection == item.id ? 'page' : undefined">
                        <component :is="item.icon" :class="[currentSection == item.id ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-500', 'mr-3 -ml-1 size-6 shrink-0']" aria-hidden="true" />
                        <span class="truncate">{{ item.name }}</span>
                        <ExclamationTriangleIcon v-if="item.id == 'savings' && isCurrentGallery && gallery.is_expired_storage" class="ml-auto size-4 text-red-600"></ExclamationTriangleIcon>
<!--                        <span v-if="item.id == 'savings'" class="ml-auto w-9 min-w-max rounded-full bg-white px-2.5 py-0.5 text-center text-xs/5 font-medium whitespace-nowrap text-gray-600 ring-1 ring-gray-200 ring-inset" aria-hidden="true">123</span>-->
                    </a>
                </nav>
            </aside>

            <div class="space-y-6 sm:px-6 lg:col-span-7 lg:px-0" v-if="isCurrentGallery">
                <form @submit.prevent="submit" v-if="currentSection == 'main'">
                    <div class="shadow-sm sm:overflow-hidden sm:rounded-md">
                        <div class="space-y-6 bg-white px-4 py-6 sm:p-6">
                            <div>
                                <h3 class="text-base font-semibold text-gray-900">{{ $t('gallery_settings.general.title') }}</h3>
                                <p class="mt-1 text-sm text-gray-500">{{ $t('gallery_settings.general.tip') }}</p>
                            </div>

                            <div class="grid grid-cols-6 gap-6">
                                <div class="col-span-6 sm:col-span-6">
                                    <label for="name" class="block text-sm/6 font-medium text-gray-900">{{ $t('gallery_settings.general.name') }}</label>
                                    <div class="mt-2 grid grid-cols-1">
                                        <input
                                            id="name"
                                            name="name"
                                            v-model="form.name"
                                            type="text"
                                            autocomplete="name"
                                            :class="{
                                            'block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6': true,
                                            'col-start-1 row-start-1 block w-full rounded-md bg-white py-1.5 pr-10 pl-3 text-base text-red-900 outline-1 -outline-offset-1 outline-red-300 placeholder:text-red-300 focus:outline-2 focus:-outline-offset-2 focus:outline-red-600 sm:pr-9 sm:text-sm/6': hasError('name')
                                        }" />
                                        <ExclamationCircleIcon v-if="hasError('name')" class="pointer-events-none col-start-1 row-start-1 mr-3 size-5 self-center justify-self-end text-red-500 sm:size-4" aria-hidden="true" />
                                    </div>
                                    <p class="mt-2 text-sm text-red-600" id="name-error" v-if="hasError('name')">{{ getError('name') }}</p>
                                </div>

                                <div class="col-span-6 sm:col-span-6">
                                    <label for="date" class="block text-sm/6 font-medium text-gray-900">{{ $t('gallery_settings.general.date') }}</label>
                                    <div class="mt-2">
                                        <DatePickerComponent
                                            id="date"
                                            v-model="form.date"
                                            :error="hasError('date')"
                                            :placeholder="$t('gallery_settings.general.date')"
                                        />
                                    </div>
                                    <p class="mt-2 text-sm text-red-600" id="date-error" v-if="hasError('date')">{{ getError('date') }}</p>
                                </div>

                                <div class="col-span-6 sm:col-span-6">
                                    <div class="flex justify-between items-center">
                                        <label for="slug" class="block text-sm/6 font-medium text-gray-900">{{ $t('gallery_settings.general.address') }}</label>
                                        <a href="#" :data-clipboard-text="gallery.public_url" class="text-sm/4 text-gray-400 hover:text-gray-500 hover:underline btn-copy">{{ $t('gallery_settings.general.copy') }}</a>
                                    </div>
                                    <div class="mt-2 grid grid-cols-1">
                                        <div :class="{
                                            'flex items-center rounded-md bg-white pl-3 outline-1 -outline-offset-1 outline-gray-300 focus-within:outline-2 focus-within:-outline-offset-2 focus-within:outline-indigo-600': true,
                                            'col-start-1 row-start-1 block w-full rounded-md text-base text-red-900 outline-red-300 placeholder:text-red-300 focus:outline-2 focus:-outline-offset-2 focus-within:outline-red-600 sm:text-sm/6': hasError('slug')
                                        }">
                                            <div class="shrink-0 text-base text-gray-500 select-none sm:text-sm/6">{{ gallery.public_url.replace(gallery.slug, '').replace('https://', '') }}</div>
                                            <input
                                                type="text"
                                                name="slug"
                                                id="slug"
                                                v-model="form.slug"
                                                :class="{
                                                'block min-w-0 grow py-1.5 pr-3 pl-1 text-base text-gray-900 placeholder:text-gray-400 focus:outline-none sm:text-sm/6': true,
                                                'text-red-900': hasError('slug')
                                            }" placeholder="some-photos" />
                                            <ExclamationCircleIcon v-if="hasError('slug')" class="pointer-events-none col-start-1 row-start-1 mr-3 size-5 self-center justify-self-end text-red-500 sm:size-4" aria-hidden="true" />
                                        </div>
                                    </div>
                                    <p class="mt-2 text-sm text-red-600" id="slug-error" v-if="hasError('slug')">{{ getError('slug') }}</p>
                                </div>

                                <div class="col-span-6 sm:col-span-6">
                                    <label for="language" class="block text-sm/6 font-medium text-gray-900">{{ $t('gallery_settings.general.language') }}</label>
                                    <div class="mt-2 grid grid-cols-1">
                                        <select
                                            id="language"
                                            name="language"
                                            v-model="form.language"
                                            :class="{
                                                'col-start-1 row-start-1 w-full appearance-none rounded-md bg-white py-1.5 pr-8 pl-3 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6': true,
                                                'col-start-1 row-start-1 block w-full rounded-md bg-white py-1.5 pr-10 pl-3 text-base text-red-900 outline-1 -outline-offset-1 outline-red-300 placeholder:text-red-300 focus:outline-2 focus:-outline-offset-2 focus:outline-red-600 sm:pr-9 sm:text-sm/6': hasError('language')
                                            }"
                                        >
                                            <option value="uk">{{ $t('gallery_settings.general.languages.ukrainian') }}</option>
                                            <option value="en">{{ $t('gallery_settings.general.languages.english') }}</option>
                                            <option value="pl">{{ $t('gallery_settings.general.languages.polish') }}</option>
                                            <option value="es">{{ $t('gallery_settings.general.languages.spanish') }}</option>
                                            <option value="de">{{ $t('gallery_settings.general.languages.german') }}</option>
                                            <option value="fr">{{ $t('gallery_settings.general.languages.french') }}</option>
                                            <option value="it">{{ $t('gallery_settings.general.languages.italian') }}</option>
                                            <option value="cz">{{ $t('gallery_settings.general.languages.czech') }}</option>
                                        </select>
                                        <ChevronDownIcon class="pointer-events-none col-start-1 row-start-1 mr-2 size-5 self-center justify-self-end text-gray-500 sm:size-4" aria-hidden="true" />
                                        <ExclamationCircleIcon v-if="hasError('language')" class="pointer-events-none col-start-1 row-start-1 mr-3 size-5 self-center justify-self-end text-red-500 sm:size-4" aria-hidden="true" />
                                    </div>
                                    <p class="mt-2 text-sm text-red-600" id="language-error" v-if="hasError('language')">{{ getError('language') }}</p>
                                </div>

                                <div class="col-span-6">
                                    <div class="flex gap-3">
                                        <div class="flex h-6 shrink-0 items-center">
                                            <div class="group grid size-4 grid-cols-1">
                                                <input
                                                    id="top_button_enabled"
                                                    aria-describedby="top_button_enabled-description"
                                                    name="top_button_enabled" type="checkbox" v-model="form.top_button_enabled"
                                                    class="col-start-1 row-start-1 appearance-none rounded-sm border border-gray-300 bg-white checked:border-blue-700 checked:bg-blue-700 indeterminate:border-indigo-600 indeterminate:bg-blue-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-700 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
                                                />
                                                <svg class="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-disabled:stroke-gray-950/25" viewBox="0 0 14 14" fill="none">
                                                    <path class="opacity-0 group-has-checked:opacity-100" d="M3 8L6 11L11 3.5" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                                                    <path class="opacity-0 group-has-indeterminate:opacity-100" d="M3 7H11" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                                                </svg>
                                            </div>
                                        </div>
                                        <div class="text-sm/6">
                                            <label for="top_button_enabled" class="font-medium text-gray-900">{{ $t('gallery_settings.general.top_button') }}</label>
                                            <p id="top_button_enabled-description" class="text-gray-500">{{ $t('gallery_settings.general.top_button_tip') }}</p>
                                        </div>
                                    </div>
                                </div>

                                <div class="col-span-6">
                                    <div class="flex gap-3">
                                        <div class="flex h-6 shrink-0 items-center">
                                            <div class="group grid size-4 grid-cols-1">
                                                <input
                                                    id="allow_caption"
                                                    aria-describedby="allow_caption-description"
                                                    name="allow_caption" type="checkbox" v-model="form.allow_caption"
                                                    class="col-start-1 row-start-1 appearance-none rounded-sm border border-gray-300 bg-white checked:border-blue-700 checked:bg-blue-700 indeterminate:border-indigo-600 indeterminate:bg-blue-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-700 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
                                                />
                                                <svg class="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-disabled:stroke-gray-950/25" viewBox="0 0 14 14" fill="none">
                                                    <path class="opacity-0 group-has-checked:opacity-100" d="M3 8L6 11L11 3.5" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                                                    <path class="opacity-0 group-has-indeterminate:opacity-100" d="M3 7H11" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                                                </svg>
                                            </div>
                                        </div>
                                        <div class="text-sm/6">
                                            <label for="allow_caption" class="font-medium text-gray-900">{{ $t('gallery_settings.general.photo_caption') }}</label>
                                            <p id="allow_caption-description" class="text-gray-500">{{ $t('gallery_settings.general.photo_caption_tip') }}</p>
                                        </div>
                                    </div>
                                </div>

                                <div class="col-span-6">
                                    <div class="flex gap-3">
                                        <div class="flex h-6 shrink-0 items-center">
                                            <div class="group grid size-4 grid-cols-1">
                                                <input
                                                    id="allow_tutorial"
                                                    aria-describedby="allow_tutorial-description"
                                                    name="allow_tutorial" type="checkbox" v-model="form.allow_tutorial"
                                                    class="col-start-1 row-start-1 appearance-none rounded-sm border border-gray-300 bg-white checked:border-blue-700 checked:bg-blue-700 indeterminate:border-indigo-600 indeterminate:bg-blue-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-700 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
                                                />
                                                <svg class="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-disabled:stroke-gray-950/25" viewBox="0 0 14 14" fill="none">
                                                    <path class="opacity-0 group-has-checked:opacity-100" d="M3 8L6 11L11 3.5" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                                                    <path class="opacity-0 group-has-indeterminate:opacity-100" d="M3 7H11" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                                                </svg>
                                            </div>
                                        </div>
                                        <div class="text-sm/6">
                                            <label for="allow_tutorial" class="font-medium text-gray-900">{{ $t('gallery_settings.general.tutorial') }}</label>
                                            <p id="allow_tutorial-description" class="text-gray-500">{{ $t('gallery_settings.general.tutorial_tip') }}</p>
                                        </div>
                                    </div>
                                </div>

                                <div class="col-span-6">
                                    <div class="flex gap-3">
                                        <div class="flex h-6 shrink-0 items-center">
                                            <div class="group grid size-4 grid-cols-1">
                                                <input
                                                    id="autoplay_video"
                                                    aria-describedby="autoplay_video-description"
                                                    name="autoplay_video" type="checkbox" v-model="form.autoplay_video"
                                                    class="col-start-1 row-start-1 appearance-none rounded-sm border border-gray-300 bg-white checked:border-blue-700 checked:bg-blue-700 indeterminate:border-indigo-600 indeterminate:bg-blue-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-700 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
                                                />
                                                <svg class="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-disabled:stroke-gray-950/25" viewBox="0 0 14 14" fill="none">
                                                    <path class="opacity-0 group-has-checked:opacity-100" d="M3 8L6 11L11 3.5" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                                                    <path class="opacity-0 group-has-indeterminate:opacity-100" d="M3 7H11" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                                                </svg>
                                            </div>
                                        </div>
                                        <div class="text-sm/6">
                                            <label for="autoplay_video" class="font-medium text-gray-900">{{ $t('gallery_settings.general.autoplay_video') }}</label>
                                            <p id="autoplay_video-description" class="text-gray-500">{{ $t('gallery_settings.general.autoplay_video_tip') }}</p>
                                        </div>
                                    </div>
                                </div>

                                <div class="col-span-6" v-if="form.face_grouping_visible">
                                    <div class="flex gap-3">
                                        <div class="flex h-6 shrink-0 items-center">
                                            <div class="group grid size-4 grid-cols-1">
                                                <input
                                                    id="face_grouping_enabled"
                                                    aria-describedby="face_grouping_enabled-description"
                                                    name="face_grouping_enabled" type="checkbox" v-model="form.face_grouping_enabled"
                                                    :disabled="!form.face_grouping_available"
                                                    class="col-start-1 row-start-1 appearance-none rounded-sm border border-gray-300 bg-white checked:border-blue-700 checked:bg-blue-700 indeterminate:border-indigo-600 indeterminate:bg-blue-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-700 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
                                                />
                                                <svg class="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-disabled:stroke-gray-950/25" viewBox="0 0 14 14" fill="none">
                                                    <path class="opacity-0 group-has-checked:opacity-100" d="M3 8L6 11L11 3.5" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                                                    <path class="opacity-0 group-has-indeterminate:opacity-100" d="M3 7H11" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                                                </svg>
                                            </div>
                                        </div>
                                        <div class="text-sm/6">
                                            <label for="face_grouping_enabled" class="font-medium text-gray-900 inline-flex items-center gap-1.5">
                                                {{ $t('gallery_settings.general.face_grouping') }}
                                                <SparklesIcon class="w-4 h-4 text-royal-blue-600" aria-label="AI" />
                                            </label>
                                            <p id="face_grouping_enabled-description" class="text-gray-500">
                                                {{ $t('gallery_settings.general.face_grouping_tip') }}
                                                <template v-if="!form.face_grouping_available">
                                                    {{ $t('gallery_settings.general.face_grouping_plan_required') }}
                                                    <router-link to="/subscription" class="text-royal-blue-600 hover:text-royal-blue-700 hover:underline font-medium">{{ $t('gallery_settings.general.face_grouping_view_plans') }}</router-link>
                                                </template>
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="bg-gray-50 px-4 py-3 text-right sm:px-6">
                            <button type="submit" class="inline-flex justify-center rounded-md bg-blue-700 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-blue-600 focus:outline-hidden cursor-pointer">{{ $t('gallery_settings.save') }}</button>
                        </div>
                    </div>
                </form>

                <form @submit.prevent="submit" v-if="currentSection == 'folders'">
                    <div class="shadow-sm sm:overflow-hidden sm:rounded-md">
                        <div class="space-y-6 bg-white px-4 py-6 sm:p-6">
                            <div>
                                <h3 class="text-base font-semibold text-gray-900">{{ $t('gallery_settings.folders.title') }}</h3>
                                <p class="mt-1 text-sm text-gray-500">{{ $t('gallery_settings.folders.tip_text') }}</p>
                            </div>

                            <div class="grid grid-cols-6 gap-6">
                                <div class="col-span-6">
                                    <div class="flex gap-3">
                                        <div class="flex h-6 shrink-0 items-center">
                                            <div class="group grid size-4 grid-cols-1">
                                                <input
                                                    id="allow_all_photos"
                                                    aria-describedby="allow_all_photos-description"
                                                    name="allow_all_photos" type="checkbox" v-model="form.allow_all_photos"
                                                    class="col-start-1 row-start-1 appearance-none rounded-sm border border-gray-300 bg-white checked:border-blue-700 checked:bg-blue-700 indeterminate:border-indigo-600 indeterminate:bg-blue-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-700 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
                                                />
                                                <svg class="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-disabled:stroke-gray-950/25" viewBox="0 0 14 14" fill="none">
                                                    <path class="opacity-0 group-has-checked:opacity-100" d="M3 8L6 11L11 3.5" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                                                    <path class="opacity-0 group-has-indeterminate:opacity-100" d="M3 7H11" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                                                </svg>
                                            </div>
                                        </div>
                                        <div class="text-sm/6">
                                            <label for="allow_all_photos" class="font-medium text-gray-900">{{ $t('gallery_settings.folders.allow_all_photos') }}</label>
                                            <p id="allow_all_photos-description" class="text-gray-500">{{ $t('gallery_settings.folders.allow_all_photos_tip') }}</p>
                                        </div>
                                    </div>
                                </div>

                                <div class="col-span-6">
                                    <div class="flex gap-3">
                                        <div class="flex h-6 shrink-0 items-center">
                                            <div class="group grid size-4 grid-cols-1">
                                                <input
                                                    id="allow_empty_folders"
                                                    aria-describedby="allow_empty_folders-description"
                                                    name="allow_empty_folders" type="checkbox" v-model="form.allow_empty_folders"
                                                    class="col-start-1 row-start-1 appearance-none rounded-sm border border-gray-300 bg-white checked:border-blue-700 checked:bg-blue-700 indeterminate:border-indigo-600 indeterminate:bg-blue-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-700 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
                                                />
                                                <svg class="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-disabled:stroke-gray-950/25" viewBox="0 0 14 14" fill="none">
                                                    <path class="opacity-0 group-has-checked:opacity-100" d="M3 8L6 11L11 3.5" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                                                    <path class="opacity-0 group-has-indeterminate:opacity-100" d="M3 7H11" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                                                </svg>
                                            </div>
                                        </div>
                                        <div class="text-sm/6">
                                            <label for="allow_empty_folders" class="font-medium text-gray-900">{{ $t('gallery_settings.folders.empty_folders') }}</label>
                                            <p id="allow_empty_folders-description" class="text-gray-500">{{ $t('gallery_settings.folders.empty_folders_tip') }}</p>
                                        </div>
                                    </div>
                                </div>

                                <div class="col-span-6">
                                    <div class="flex gap-3">
                                        <div class="flex h-6 shrink-0 items-center">
                                            <div class="group grid size-4 grid-cols-1">
                                                <input
                                                    id="allow_list_view"
                                                    aria-describedby="allow_list_view-description"
                                                    name="allow_list_view" type="checkbox" v-model="form.allow_list_view"
                                                    class="col-start-1 row-start-1 appearance-none rounded-sm border border-gray-300 bg-white checked:border-blue-700 checked:bg-blue-700 indeterminate:border-indigo-600 indeterminate:bg-blue-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-700 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
                                                />
                                                <svg class="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-disabled:stroke-gray-950/25" viewBox="0 0 14 14" fill="none">
                                                    <path class="opacity-0 group-has-checked:opacity-100" d="M3 8L6 11L11 3.5" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                                                    <path class="opacity-0 group-has-indeterminate:opacity-100" d="M3 7H11" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                                                </svg>
                                            </div>
                                        </div>
                                        <div class="text-sm/6">
                                            <label for="allow_list_view" class="font-medium text-gray-900">{{ $t('gallery_settings.folders.allow_list_view') }}</label>
                                            <p id="allow_list_view-description" class="text-gray-500">{{ $t('gallery_settings.folders.allow_list_view_tip') }}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="bg-gray-50 px-4 py-3 text-right sm:px-6">
                            <button type="submit" class="inline-flex justify-center rounded-md bg-blue-700 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-blue-600 focus:outline-hidden cursor-pointer">{{ $t('gallery_settings.save') }}</button>
                        </div>
                    </div>
                </form>

                <form @submit.prevent="submit" v-if="currentSection == 'favorites'">
                    <div class="shadow-sm sm:overflow-hidden sm:rounded-md">
                        <div class="space-y-6 bg-white px-4 py-6 sm:p-6">
                            <div>
                                <h3 class="text-base font-semibold text-gray-900">{{ $t('gallery_settings.favorites.title') }}</h3>
                                <p class="mt-1 text-sm text-gray-500">{{ $t('gallery_settings.favorites.tip_text') }}</p>
                            </div>

                            <div class="grid grid-cols-6 gap-6">
                                <div class="col-span-6">
                                    <div class="flex gap-3">
                                        <div class="flex h-6 shrink-0 items-center">
                                            <div class="group grid size-4 grid-cols-1">
                                                <input
                                                    id="allow_favorite"
                                                    aria-describedby="allow_all_photos-description"
                                                    name="allow_favorite" type="checkbox" v-model="form.allow_favorite"
                                                    class="col-start-1 row-start-1 appearance-none rounded-sm border border-gray-300 bg-white checked:border-blue-700 checked:bg-blue-700 indeterminate:border-indigo-600 indeterminate:bg-blue-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-700 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
                                                />
                                                <svg class="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-disabled:stroke-gray-950/25" viewBox="0 0 14 14" fill="none">
                                                    <path class="opacity-0 group-has-checked:opacity-100" d="M3 8L6 11L11 3.5" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                                                    <path class="opacity-0 group-has-indeterminate:opacity-100" d="M3 7H11" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                                                </svg>
                                            </div>
                                        </div>
                                        <div class="text-sm/6">
                                            <label for="allow_favorite" class="font-medium text-gray-900">{{ $t('gallery_settings.favorites.allow_favorite') }}</label>
                                            <p id="allow_favorite-description" class="text-gray-500">{{ $t('gallery_settings.favorites.allow_favorite_tip') }}</p>
                                        </div>
                                    </div>
                                </div>

                                <div class="col-span-6">
                                    <div class="flex gap-3">
                                        <div class="flex h-6 shrink-0 items-center">
                                            <div class="group grid size-4 grid-cols-1">
                                                <input
                                                    id="is_need_to_finish_favorites"
                                                    aria-describedby="is_need_to_finish_favorites-description"
                                                    name="is_need_to_finish_favorites" type="checkbox" v-model="form.is_need_to_finish_favorites"
                                                    class="col-start-1 row-start-1 appearance-none rounded-sm border border-gray-300 bg-white checked:border-blue-700 checked:bg-blue-700 indeterminate:border-indigo-600 indeterminate:bg-blue-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-700 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
                                                />
                                                <svg class="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-disabled:stroke-gray-950/25" viewBox="0 0 14 14" fill="none">
                                                    <path class="opacity-0 group-has-checked:opacity-100" d="M3 8L6 11L11 3.5" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                                                    <path class="opacity-0 group-has-indeterminate:opacity-100" d="M3 7H11" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                                                </svg>
                                            </div>
                                        </div>
                                        <div class="text-sm/6">
                                            <label for="is_need_to_finish_favorites" class="font-medium text-gray-900">{{ $t('gallery_settings.favorites.is_need_to_finish_favorites') }}</label>
                                            <p id="is_need_to_finish_favorites-description" class="text-gray-500">{{ $t('gallery_settings.favorites.is_need_to_finish_favorites_tip') }}</p>
                                        </div>
                                    </div>
                                </div>

                                <div class="col-span-6">
                                    <div class="flex gap-3">
                                        <div class="flex h-6 shrink-0 items-center">
                                            <div class="group grid size-4 grid-cols-1">
                                                <input
                                                    id="allow_limit_favorites"
                                                    aria-describedby="allow_limit_favorites-description"
                                                    name="allow_limit_favorites" type="checkbox" v-model="form.allow_limit_favorites"
                                                    class="col-start-1 row-start-1 appearance-none rounded-sm border border-gray-300 bg-white checked:border-blue-700 checked:bg-blue-700 indeterminate:border-indigo-600 indeterminate:bg-blue-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-700 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
                                                />
                                                <svg class="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-disabled:stroke-gray-950/25" viewBox="0 0 14 14" fill="none">
                                                    <path class="opacity-0 group-has-checked:opacity-100" d="M3 8L6 11L11 3.5" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                                                    <path class="opacity-0 group-has-indeterminate:opacity-100" d="M3 7H11" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                                                </svg>
                                            </div>
                                        </div>
                                        <div class="text-sm/6">
                                            <label for="allow_limit_favorites" class="font-medium text-gray-900">{{ $t('gallery_settings.favorites.allow_limit_favorites') }}</label>
                                            <p id="allow_limit_favorites-description" class="text-gray-500">{{ $t('gallery_settings.favorites.allow_limit_favorites_tip') }}</p>
                                        </div>

                                        <input
                                            type="number"
                                            name="limit_favorites"
                                            id="limit_favorites"
                                            v-if="form.allow_limit_favorites"
                                            v-model="form.limit_favorites"
                                            min="0"
                                            :max="gallery.photos_count"
                                            class="hidden sm:block w-16 ms-auto text-center rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:max-w-xs sm:text-sm/6"
                                        />
                                    </div>

                                    <div v-if="form.allow_limit_favorites" class="grid sm:hidden sm:grid-cols-3 sm:items-start sm:gap-4 py-1 sm:py-3">
                                        <label for="limit_favorites" class="block text-sm/6 font-medium text-gray-900 sm:pt-1.5">{{ $t('gallery_settings.favorites.allow_limit_favorites_count') }}</label>
                                        <div class="mt-2 sm:col-span-2 sm:mt-0">
                                            <input
                                                type="number"
                                                name="limit_favorites"
                                                id="limit_favorites"
                                                v-model="form.limit_favorites"
                                                class="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:max-w-xs sm:text-sm/6"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="bg-gray-50 px-4 py-3 text-right sm:px-6">
                            <button type="submit" class="inline-flex justify-center rounded-md bg-blue-700 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-blue-600 focus:outline-hidden cursor-pointer">{{ $t('gallery_settings.save') }}</button>
                        </div>
                    </div>
                </form>

                <form @submit.prevent="submit" v-if="currentSection == 'savings'">
                    <div class="shadow-sm sm:overflow-hidden sm:rounded-md">
                        <div class="space-y-6 bg-white px-4 py-6 sm:p-6">
                            <div>
                                <h3 class="text-base font-semibold text-gray-900">{{ $t('gallery_settings.savings.title') }}</h3>
                                <p class="mt-1 text-sm text-gray-500">{{ $t('gallery_settings.savings.tip_text') }}</p>
                            </div>

                            <div class="grid grid-cols-6 gap-6">
                                <div class="col-span-6">
                                    <div class="flex gap-3">
                                        <div class="flex h-6 shrink-0 items-center">
                                            <div class="group grid size-4 grid-cols-1">
                                                <input
                                                    id="allow_download"
                                                    aria-describedby="allow_download-description"
                                                    name="allow_download" type="checkbox" v-model="form.allow_download"
                                                    class="col-start-1 row-start-1 appearance-none rounded-sm border border-gray-300 bg-white checked:border-blue-700 checked:bg-blue-700 indeterminate:border-indigo-600 indeterminate:bg-blue-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-700 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
                                                />
                                                <svg class="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-disabled:stroke-gray-950/25" viewBox="0 0 14 14" fill="none">
                                                    <path class="opacity-0 group-has-checked:opacity-100" d="M3 8L6 11L11 3.5" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                                                    <path class="opacity-0 group-has-indeterminate:opacity-100" d="M3 7H11" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                                                </svg>
                                            </div>
                                        </div>
                                        <div class="text-sm/6">
                                            <label for="allow_download" class="font-medium text-gray-900">{{ $t('gallery_settings.savings.allow_download') }}</label>
                                            <p id="allow_download-description" class="text-gray-500">{{ $t('gallery_settings.savings.allow_download_tip') }}</p>
                                        </div>
                                    </div>
                                </div>

                                <div class="col-span-6" v-if="gallery.is_expired_storage">
                                    <div class="rounded-md bg-yellow-50 p-4">
                                        <div class="flex">
                                            <div class="shrink-0">
                                                <ExclamationTriangleIcon class="size-5 text-yellow-400" aria-hidden="true" />
                                            </div>
                                            <div class="ml-3">
                                                <h3 class="text-sm font-medium text-yellow-800">{{ $t('gallery_settings.savings.is_expired_storage') }}</h3>
                                                <div class="mt-2 text-sm text-yellow-700">
                                                    <p>{{ $t('gallery_settings.savings.is_expired_storage_tip') }}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div class="col-span-6 sm:col-span-6">
                                    <div class="flex justify-between items-center">
                                        <label for="storage_to_date" class="block text-sm/6 font-medium text-gray-900">{{ $t('gallery_settings.savings.storage_to') }}</label>
                                        <span
                                            v-if="form.storage_to_date !== null"
                                            @click.prevent="resetStorageToDate"
                                            class="text-sm/4 text-gray-400 hover:text-gray-500 hover:underline cursor-pointer"
                                        >
                                            {{ $t('gallery_settings.savings.unlimited_storage') }}
                                        </span>
                                    </div>
                                    <div class="mt-2">
                                        <DatePickerComponent
                                            id="storage_to_date"
                                            v-model="form.storage_to_date"
                                            :error="hasError('storage_to_date')"
                                            :min="todayIso"
                                            :placeholder="$t('gallery_settings.savings.storage_to')"
                                            :empty-label="$t('gallery_settings.savings.unlimited_storage')"
                                        />
                                    </div>
                                    <p class="mt-2 text-sm text-red-600" id="storage_to_date-error" v-if="hasError('storage_to_date')">{{ getError('storage_to_date') }}</p>
                                    <p class="mt-1 text-sm/6 text-gray-400">{{ $t('gallery_settings.savings.storage_to_tip') }}</p>
                                </div>
                            </div>
                        </div>
                        <div class="bg-gray-50 px-4 py-3 text-right sm:px-6">
                            <div class="inline-flex justify-between w-full">
                                <button type="button" @click.prevent="deleteGallery" class="inline-flex justify-center rounded-md bg-red-500 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-red-600 focus:outline-hidden cursor-pointer">{{ $t('gallery_settings.savings.delete_gallery') }}</button>

                                <button type="submit" class="inline-flex justify-center rounded-md bg-royal-blue-500 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-royal-blue-600 focus:outline-hidden cursor-pointer">{{ $t('gallery_settings.save') }}</button>
                            </div>
                        </div>
                    </div>
                </form>

                <GalleryStatisticsComponent v-if="currentSection == 'statistics'" :gallery="gallery" />

                <form @submit.prevent="submit" v-if="currentSection == 'access'">
                    <div class="shadow-sm sm:overflow-hidden sm:rounded-md">
                        <div class="space-y-6 bg-white px-4 py-6 sm:p-6">
                            <div>
                                <h3 class="text-base font-semibold text-gray-900">{{ $t('gallery_settings.access.title') }}</h3>
                                <p class="mt-1 text-sm text-gray-500">{{ $t('gallery_settings.access.tip_text') }}</p>
                            </div>

                            <div class="grid grid-cols-6 gap-6">
                                <div class="col-span-6">
                                    <div class="flex gap-3">
                                        <div class="flex h-6 shrink-0 items-center">
                                            <div class="group grid size-4 grid-cols-1">
                                                <input
                                                    id="is_public"
                                                    aria-describedby="allow_download-description"
                                                    name="is_public" type="checkbox" v-model="form.is_public"
                                                    class="col-start-1 row-start-1 appearance-none rounded-sm border border-gray-300 bg-white checked:border-blue-700 checked:bg-blue-700 indeterminate:border-indigo-600 indeterminate:bg-blue-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-700 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
                                                />
                                                <svg class="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-disabled:stroke-gray-950/25" viewBox="0 0 14 14" fill="none">
                                                    <path class="opacity-0 group-has-checked:opacity-100" d="M3 8L6 11L11 3.5" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                                                    <path class="opacity-0 group-has-indeterminate:opacity-100" d="M3 7H11" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                                                </svg>
                                            </div>
                                        </div>
                                        <div class="text-sm/6">
                                            <label for="is_public" class="font-medium text-gray-900">{{ $t('gallery_settings.access.is_public') }}</label>
                                            <p id="is_public-description" class="text-gray-500">{{ $t('gallery_settings.access.is_public_tip') }}</p>
                                        </div>
                                    </div>
                                </div>

                                <div class="col-span-6 sm:col-span-6" v-if="!form.is_public">
                                    <div class="flex justify-between items-center">
                                        <label for="storage_to_date" class="block text-sm/6 font-medium text-gray-900">{{ $t('gallery_settings.access.password') }}</label>
                                        <span
                                            :data-clipboard-text="form.password"
                                            class="text-sm/4 text-gray-400 hover:text-gray-500 hover:underline cursor-pointer btn-copy"
                                        >
                                            {{ $t('gallery_settings.access.copy') }}
                                        </span>
                                    </div>
                                    <div class="mt-2 grid grid-cols-1">
                                        <div class="flex">
                                            <div class="-mr-px grid grow grid-cols-1 focus-within:relative">
                                                <input
                                                    id="password"
                                                    name="password"
                                                    v-model="form.password"
                                                    type="text"
                                                    autocomplete="password"
                                                    :class="{
                                                        'block w-full rounded-l-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6': true,
                                                        'col-start-1 row-start-1 block w-full rounded-l-md bg-white py-1.5 pr-10 pl-3 text-base text-red-900 outline-1 -outline-offset-1 outline-red-300 placeholder:text-red-300 focus:outline-2 focus:-outline-offset-2 focus:outline-red-600 sm:pr-9 sm:text-sm/6': hasError('storage_to_date')
                                                    }"
                                                />
                                            </div>
                                            <button type="button" @click="generatePassword" class="flex shrink-0 items-center gap-x-1.5 rounded-r-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 outline-1 -outline-offset-1 outline-gray-300 hover:bg-gray-50 focus:relative focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 cursor-pointer">
                                                <ArrowPathRoundedSquareIcon class="-ml-0.5 size-4" aria-hidden="true" />
                                            </button>
                                        </div>
                                        <ExclamationCircleIcon v-if="hasError('password')" class="pointer-events-none col-start-1 row-start-1 mr-3 size-5 self-center justify-self-end text-red-500 sm:size-4" aria-hidden="true" />
                                    </div>
                                    <p class="mt-2 text-sm text-red-600" id="storage_to_date-error" v-if="hasError('password')">{{ getError('password') }}</p>
                                </div>

                                <div class="col-span-6 sm:col-span-6" v-if="form.config.hide_photos">
                                    <div class="flex gap-3">
                                        <div class="flex h-6 shrink-0 items-center">
                                            <div class="group grid size-4 grid-cols-1">
                                                <input
                                                    id="allow_photo_hiding"
                                                    aria-describedby="allow_photo_hiding-description"
                                                    name="allow_photo_hiding" type="checkbox" v-model="form.allow_photo_hiding"
                                                    class="col-start-1 row-start-1 appearance-none rounded-sm border border-gray-300 bg-white checked:border-blue-700 checked:bg-blue-700 indeterminate:border-indigo-600 indeterminate:bg-blue-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-700 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
                                                />
                                                <svg class="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-checked:opacity-100" viewBox="0 0 14 14" fill="none">
                                                    <path class="opacity-0 group-has-checked:opacity-100" d="M3 8L6 11L11 3.5" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                                                    <path class="opacity-0 group-has-indeterminate:opacity-100" d="M3 7H11" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                                                </svg>
                                            </div>
                                        </div>
                                        <div class="text-sm/6">
                                            <label for="allow_photo_hiding" class="font-medium text-gray-900">{{ $t('gallery_settings.access.photo_hiding') }}</label>
                                            <p id="allow_photo_hiding-description" class="text-gray-500">{{ $t('gallery_settings.access.photo_hiding_tip') }}</p>
                                        </div>
                                    </div>
                                </div>

                                <div class="col-span-6 sm:col-span-6" v-if="form.allow_photo_hiding">
                                    <label for="photo_hiding_client_email" class="block text-sm/6 font-medium text-gray-900">{{ $t('gallery_settings.access.client_email') }}</label>
                                    <div class="mt-2 grid grid-cols-1">
                                        <input
                                            id="photo_hiding_client_email"
                                            name="photo_hiding_client_email"
                                            v-model="form.photo_hiding_client_email"
                                            type="email"
                                            autocomplete="email"
                                            :class="{
                                                'block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6': true,
                                                'col-start-1 row-start-1 block w-full rounded-md bg-white py-1.5 pr-10 pl-3 text-base text-red-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-red-300 focus:outline-2 focus:-outline-offset-2 focus:outline-red-600 sm:pr-9 sm:text-sm/6': hasError('photo_hiding_client_email')
                                            }" placeholder="client@example.com" />
                                        <ExclamationCircleIcon v-if="hasError('photo_hiding_client_email')" class="pointer-events-none col-start-1 row-start-1 mr-3 size-5 self-center justify-self-end text-red-500 sm:size-4" aria-hidden="true" />
                                    </div>
                                    <p class="mt-2 text-sm text-red-600" id="photo_hiding_client_email-error" v-if="hasError('photo_hiding_client_email')">{{ getError('photo_hiding_client_email') }}</p>
                                    <p class="mt-1 text-sm/6 text-gray-400">{{ $t('gallery_settings.access.client_email_tip') }}</p>
                                </div>
                            </div>
                        </div>
                        <div class="bg-gray-50 px-4 py-3 text-right sm:px-6">
                            <button type="submit" class="inline-flex justify-center rounded-md bg-blue-700 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-blue-600 focus:outline-hidden cursor-pointer">{{ $t('gallery_settings.save') }}</button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    </div>

    <DeleteGalleryConfirmationModalComponent
        :open="isDeletingConfirmation"
        @close="isDeletingConfirmation = false"
        @confirmAction="doDeleteGallery"
        :title="$t('gallery_settings.delete_gallery.title')"
        :message="$t('gallery_settings.delete_gallery.confirmation')"
        :secondMessage="deletingConfirmationWarning"
        :buttonText="$t('gallery_settings.delete_gallery.button')"
    />
</template>

<script lang="ts">
import {defineComponent} from 'vue';

import {Gallery} from "@/types/GalleryType";
import {validationMethods} from "@/_helpers/validation";
import {mapActions, mapGetters} from "vuex";

import ControlsMenuComponent from "@/components/Galleries/ControlsMenuComponent.vue";

import {deleteGallery, update} from "@/data/requests/gallery";
import {ResponseError} from "@/types";

import {ExclamationCircleIcon, ChevronDownIcon} from "@heroicons/vue/16/solid";
import {SparklesIcon} from "@heroicons/vue/20/solid";
import {
    CogIcon,
    BookmarkIcon,
    CloudIcon,
    LockClosedIcon,
    FolderIcon,
    ArrowPathRoundedSquareIcon,
    ExclamationTriangleIcon,
    ChartBarIcon
} from "@heroicons/vue/24/outline";
import DeleteGalleryConfirmationModalComponent
    from "@/components/Galleries/Modals/DeleteGalleryConfirmationModalComponent.vue";
import DeletePhotosConfirmationModalComponent
    from "@/components/Galleries/Modals/DeletePhotosConfirmationModalComponent.vue";
import GalleryStatisticsComponent
    from "@/components/Galleries/GalleryStatisticsComponent.vue";
import DatePickerComponent from "@/components/UI/DatePickerComponent.vue";
import moment from "moment";

export default defineComponent({
    name: "SettingsPage",

    components: {
        DeletePhotosConfirmationModalComponent,
        DeleteGalleryConfirmationModalComponent,
        ExclamationCircleIcon,
        ChevronDownIcon,
        ArrowPathRoundedSquareIcon,
        ExclamationTriangleIcon,
        SparklesIcon,
        ControlsMenuComponent,
        GalleryStatisticsComponent,
        DatePickerComponent
    },

    data() {
        return {
            isLoadingGallery: false,

            isGalleryUpdated: false,

            form: {} as Gallery,

            errors: { } as { [key: string]: string[] },

            currentSection: "main",

            subNavigation: [
                { name: this.$t('gallery_settings.navigation.general'), id: 'main', icon: CogIcon, },
                { name: this.$t('gallery_settings.navigation.folders'), id: 'folders', icon: FolderIcon, },
                { name: this.$t('gallery_settings.navigation.favorites'), id: 'favorites', icon: BookmarkIcon, },
                { name: this.$t('gallery_settings.navigation.savings'), id: 'savings', icon: CloudIcon, },
                { name: this.$t('gallery_settings.navigation.access'), id: 'access', icon: LockClosedIcon, },
                { name: this.$t('gallery_settings.navigation.statistics'), id: 'statistics', icon: ChartBarIcon, },
            ],

            isDeletingConfirmation: false,
        };
    },

    computed: {
        ...mapGetters({
            gallery: 'galleries/gallery',

            user: "user/user",
        }),

        currentSlug(): string {
            return this.$route.params.gallery as string;
        },

        isCurrentGallery(): boolean {
            return this.gallery != null && this.gallery.slug == this.currentSlug;
        },

        deletingConfirmationWarning(): string {
            if (!this.isCurrentGallery) return '';

            const downloads = this.gallery.downloads_count || 0;
            const filesDownloads = this.gallery.files_downloads_count || 0;

            if (downloads === 0 && filesDownloads === 0) {
                return this.$t('gallery_settings.delete_gallery.never_downloaded');
            }

            if (downloads === 0 && filesDownloads > 0) {
                return this.$t('gallery_settings.delete_gallery.partially_downloaded');
            }

            return '';
        },

        todayIso(): string {
            return moment().format('YYYY-MM-DD');
        }
    },

    mounted() {
        if (this.isCurrentGallery) {
            this.form = {...this.gallery};

            return;
        }

        this.fetchGallery();
    },

    watch: {
        'form.name': function () {
            this.hideGalleryUpdatedMessage();

            delete this.errors['name'];
        },
        'form.date': function () {
            this.hideGalleryUpdatedMessage();

            delete this.errors['date'];
        },
        'form.slug': function () {
            this.hideGalleryUpdatedMessage();

            delete this.errors['slug'];
        },
        'form.storage_to_date': function () {
            this.hideGalleryUpdatedMessage();

            delete this.errors['storage_to_date'];
        },
        'form.allow_download': function () {
            this.hideGalleryUpdatedMessage();

            delete this.errors['allow_download'];
        },
        'form.allow_favorite': function () {
            this.hideGalleryUpdatedMessage();

            delete this.errors['allow_favorite'];
        },
        'form.allow_caption': function () {
            this.hideGalleryUpdatedMessage();

            delete this.errors['allow_caption'];
        },
        'form.allow_all_photos': function () {
            this.hideGalleryUpdatedMessage();

            delete this.errors['allow_all_photos'];
        },
        'form.allow_empty_folders': function () {
            this.hideGalleryUpdatedMessage();

            delete this.errors['allow_empty_folders'];
        },
        'form.allow_list_view': function () {
            this.hideGalleryUpdatedMessage();

            delete this.errors['allow_list_view'];
        },
        'form.top_button_enabled': function () {
            this.hideGalleryUpdatedMessage();

            delete this.errors['top_button_enabled'];
        },
        'form.allow_tutorial': function () {
            this.hideGalleryUpdatedMessage();

            delete this.errors['allow_tutorial'];
        },
        'form.language': function () {
            this.hideGalleryUpdatedMessage();

            delete this.errors['language'];
        },
        'form.is_public': function () {
            this.hideGalleryUpdatedMessage();

            delete this.errors['is_public'];
        },
        'form.password': function () {
            this.hideGalleryUpdatedMessage();

            delete this.errors['password'];
        },
    },

    methods: {
        ...mapActions({
            loadGallery: "galleries/loadGallery",
            loadGalleries: "galleries/loadGalleries",
            fetchUser: "user/fetchUser",

            errorAlert: "alerts/error",
            successAlert: "alerts/success",
        }),

        ...validationMethods(),

        fetchGallery(slug: string | null = null) {
            if (!this.isCurrentGallery) {
                this.isLoadingGallery = true;
            }

            if (slug == null) {
                slug = this.currentSlug;
            }

            this.loadGallery({slug})
                .then((gallery: Gallery) => {
                    this.form = {...gallery};

                    this.isLoadingGallery = false;
                })
                .catch(({data}) => {
                    this.errorAlert({
                        message: this.$t('gallery_settings.errors.gallery_loading', {message: data.message}),
                    });

                    this.isLoadingGallery = false;
                });
        },

        submit() {
            update(this.gallery.slug, this.form, ({status, data}) => {
                if (status === "success") {
                    this.isGalleryUpdated = true;

                    this.successAlert({
                        message: this.$t('gallery_settings.success_saved'),
                        callback: () => {
                            this.isGalleryUpdated = false;
                        }
                    });
                }

                if (data.slug != this.gallery.slug) {
                    this.$router.push(`/galleries/${data.slug}/settings`);
                }

                this.fetchGallery(data.slug);
            }, (type, data) => {
                if (type == ResponseError.Validation) {
                    this.errors = data.data;

                    this.errorAlert({
                        message: this.$t('create_gallery.errors.validation_error'),
                    });
                } else {
                    this.errorAlert({
                        message: data.message,
                    });
                }
            })
        },

        resetStorageToDate() {
            this.form.storage_to_date = null;
        },

        generatePassword() {
            this.form.password = Math.random().toString(36).slice(-6);
        },

        hideGalleryUpdatedMessage() {
            this.isGalleryUpdated = false;
        },

        deleteGallery() {
            this.isDeletingConfirmation = true;
        },

        doDeleteGallery() {
            deleteGallery(this.currentSlug, () => {
                this.loadGalleries();
                this.fetchUser({token: ''});

                this.$router.push(`/`);
            }, (type, data) => {
                this.errorAlert({
                    message: this.$t('gallery_settings.errors.delete_error', {message: data.message}),
                });
            });
        }
    }
})
</script>

<style lang="scss" scoped>
.form-control {
    &.is-expired {
        outline: 1px solid #dc3545!important;
    }
}
</style>